import { useState, useEffect, useRef } from 'react'
import { ArrowLeft, ArrowRight, Upload, Users, X, Database, FileSpreadsheet, CheckCircle, Tags, AlertCircle } from 'lucide-react'
import Papa from 'papaparse'
import axios from '../../lib/axios'
import './AICreateCampaign.css'
import '../Pages.css'

const STANDARD_VARIABLES = [
  { key: 'name', label: 'Full Name' },
  { key: 'first_name', label: 'First Name' },
  { key: 'last_name', label: 'Last Name' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone' },
  { key: 'location', label: 'Location' },
  { key: 'linkedin', label: 'LinkedIn' },
  { key: 'facebook', label: 'Facebook' },
]

const FIELD_OPTIONS = [
  'Do not import',
  'Email',
  'Name',
  'Phone',
  'Location',
  'Linkedin',
  'Facebook',
  'Custom variable',
]

export default function LeadSelectionStep({ onBack, onNext, selectedLeads, onLeadsChange, availableVariables, onVariablesChange }) {
  const [tab, setTab] = useState('upload') // 'upload' | 'existing'
  const [contacts, setContacts] = useState([])
  const [loadingContacts, setLoadingContacts] = useState(false)
  const [selectedContactIds, setSelectedContactIds] = useState(new Set())
  const [searchTerm, setSearchTerm] = useState('')

  // CSV upload states
  const fileInputRef = useRef(null)
  const [columns, setColumns] = useState([])
  const [uploadedRows, setUploadedRows] = useState([])
  const [csvFileName, setCsvFileName] = useState('')

  // Load existing contacts on mount
  useEffect(() => {
    fetchContacts()
  }, [])

  // Restore selected contacts from parent state
  useEffect(() => {
    if (selectedLeads?.source === 'existing' && selectedLeads?.contactIds) {
      setSelectedContactIds(new Set(selectedLeads.contactIds))
      setTab('existing')
    } else if (selectedLeads?.source === 'csv' && selectedLeads?.rows) {
      setUploadedRows(selectedLeads.rows)
      setColumns(selectedLeads.columns || [])
      setCsvFileName(selectedLeads.fileName || '')
      setTab('upload')
    }
  }, [])

  const fetchContacts = async () => {
    setLoadingContacts(true)
    try {
      const res = await axios.get('/contacts')
      setContacts(Array.isArray(res.data) ? res.data : [])
    } catch {
      setContacts([])
    } finally {
      setLoadingContacts(false)
    }
  }

  const handleFileChange = (event) => {
    const file = event.target.files[0]
    if (!file) return
    event.target.value = ''
    setCsvFileName(file.name)

    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const data = results.data
        setUploadedRows(data)

        if (data.length > 0) {
          const headers = Object.keys(data[0])
          const columnsWithSamples = headers.map((header) => ({
            name: header,
            samples: data.slice(0, 3).map((row) => (row[header] ? row[header].toString().trim() : '')),
            selectedType: autoDetectColumnType(header),
          }))
          setColumns(columnsWithSamples)
        }
      },
    })
  }

  // Auto-detect column type from header name
  const autoDetectColumnType = (header) => {
    const h = header.toLowerCase().trim()
    if (h.includes('email') || h.includes('e-mail')) return 'Email'
    if (h === 'name' || h === 'full name' || h === 'fullname') return 'Name'
    if (h.includes('phone') || h.includes('mobile') || h.includes('tel')) return 'Phone'
    if (h.includes('location') || h.includes('city') || h.includes('country') || h.includes('address')) return 'Location'
    if (h.includes('linkedin')) return 'Linkedin'
    if (h.includes('facebook') || h.includes('fb')) return 'Facebook'
    return 'Custom variable'
  }

  const getUsedTypes = () =>
    columns
      .map((c) => c.selectedType)
      .filter((type) => type !== 'Do not import' && type !== 'Custom variable')

  // Extract variable names from CSV mapping
  const extractCsvVariables = () => {
    const vars = []
    columns.forEach((col) => {
      if (col.selectedType === 'Do not import') return
      if (col.selectedType === 'Custom variable') {
        // Use CSV column header as custom variable name
        const varName = col.name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
        if (varName) vars.push({ key: varName, label: col.name, custom: true })
      } else {
        // Standard field
        const key = col.selectedType.toLowerCase()
        const std = STANDARD_VARIABLES.find(v => v.key === key)
        if (std) vars.push({ ...std })
        // Name also provides first_name and last_name
        if (key === 'name') {
          vars.push({ key: 'first_name', label: 'First Name' })
          vars.push({ key: 'last_name', label: 'Last Name' })
        }
      }
    })
    return vars
  }

  // Extract variable names from existing contacts
  const extractContactVariables = () => {
    const vars = [...STANDARD_VARIABLES.filter(v => ['name', 'first_name', 'last_name', 'email'].includes(v.key))]
    const selected = contacts.filter(c => selectedContactIds.has(c.id))

    // Check which standard fields have data
    const hasPhone = selected.some(c => c.phone)
    const hasLocation = selected.some(c => c.location)
    const hasLinkedin = selected.some(c => c.linkedin)
    const hasFacebook = selected.some(c => c.facebook)

    if (hasPhone) vars.push({ key: 'phone', label: 'Phone' })
    if (hasLocation) vars.push({ key: 'location', label: 'Location' })
    if (hasLinkedin) vars.push({ key: 'linkedin', label: 'LinkedIn' })
    if (hasFacebook) vars.push({ key: 'facebook', label: 'Facebook' })

    // Collect custom field keys
    const customKeys = new Set()
    selected.forEach(c => {
      if (c.custom_fields) {
        Object.keys(c.custom_fields).forEach(k => customKeys.add(k))
      }
    })
    customKeys.forEach(k => {
      vars.push({ key: k, label: k, custom: true })
    })

    return vars
  }

  const toggleContact = (id) => {
    setSelectedContactIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = () => {
    const filtered = filteredContacts
    if (selectedContactIds.size === filtered.length) {
      setSelectedContactIds(new Set())
    } else {
      setSelectedContactIds(new Set(filtered.map(c => c.id)))
    }
  }

  const filteredContacts = contacts.filter(c =>
    !c.unsubscribed &&
    ((c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
     (c.email || '').toLowerCase().includes(searchTerm.toLowerCase()))
  )

  const handleNext = () => {
    let vars = []
    let leadsData = {}

    if (tab === 'upload' && uploadedRows.length > 0) {
      vars = extractCsvVariables()
      leadsData = {
        source: 'csv',
        rows: uploadedRows,
        columns,
        fileName: csvFileName,
        count: uploadedRows.length,
      }
    } else if (tab === 'existing' && selectedContactIds.size > 0) {
      vars = extractContactVariables()
      leadsData = {
        source: 'existing',
        contactIds: Array.from(selectedContactIds),
        contacts: contacts.filter(c => selectedContactIds.has(c.id)),
        count: selectedContactIds.size,
      }
    }

    onLeadsChange(leadsData)
    onVariablesChange(vars)
    onNext()
  }

  const canProceed =
    (tab === 'upload' && uploadedRows.length > 0 && columns.some(c => c.selectedType === 'Email')) ||
    (tab === 'existing' && selectedContactIds.size > 0)

  // Show current variables preview
  const currentVars = tab === 'upload' && uploadedRows.length > 0
    ? extractCsvVariables()
    : tab === 'existing' && selectedContactIds.size > 0
    ? extractContactVariables()
    : []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: 'var(--color-text-primary)' }}>
            Add Your Leads
          </h3>
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: '0.25rem 0 0' }}>
            Upload leads or select existing contacts — AI will use their fields as personalisation variables
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={onBack} className="btn btn-ghost">
            <ArrowLeft size={16} />
            Back
          </button>
          <button onClick={handleNext} disabled={!canProceed} className="btn btn-primary">
            Next
            <ArrowRight size={16} />
          </button>
        </div>
      </div>

      {/* Tab Switcher */}
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button
          onClick={() => setTab('upload')}
          className={`campaign-filter-chip${tab === 'upload' ? ' active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}
        >
          <FileSpreadsheet size={15} />
          Upload CSV
        </button>
        <button
          onClick={() => setTab('existing')}
          className={`campaign-filter-chip${tab === 'existing' ? ' active' : ''}`}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 1rem' }}
        >
          <Database size={15} />
          Existing Contacts ({contacts.length})
        </button>
      </div>

      {/* Upload CSV Tab */}
      {tab === 'upload' && (
        <div className="card">
          <div className="card-header">
            <h3><Upload size={17} /> Upload Lead CSV</h3>
          </div>
          <div className="card-body">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: 'none' }}
              onChange={handleFileChange}
            />

            {columns.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                <Upload size={42} style={{ margin: '0 auto 0.75rem', color: 'var(--color-text-muted)', display: 'block' }} />
                <p style={{ marginBottom: '1rem', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
                  Upload a CSV file with your lead data
                </p>
                <button onClick={() => fileInputRef.current.click()} className="btn btn-primary">
                  <Upload size={16} />
                  Browse CSV
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                    <FileSpreadsheet size={15} />
                    <strong>{csvFileName}</strong> — {uploadedRows.length} rows
                  </div>
                  <button
                    onClick={() => { setColumns([]); setUploadedRows([]); setCsvFileName('') }}
                    className="btn btn-ghost"
                    style={{ fontSize: '0.8rem' }}
                  >
                    Change File
                  </button>
                </div>

                <div className="table-container" style={{ maxHeight: '40vh', overflow: 'auto' }}>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>CSV Column</th>
                        <th>Map To</th>
                        <th>Sample Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {columns.map((col, idx) => {
                        const usedTypes = getUsedTypes()
                        return (
                          <tr key={idx}>
                            <td style={{ fontWeight: 600 }}>{col.name}</td>
                            <td>
                              <select
                                value={col.selectedType}
                                onChange={(e) => {
                                  const newCols = [...columns]
                                  newCols[idx].selectedType = e.target.value
                                  setColumns(newCols)
                                }}
                                style={{
                                  width: '100%',
                                  padding: '0.5rem',
                                  background: 'var(--color-bg-primary)',
                                  border: '1px solid var(--color-border)',
                                  borderRadius: 'var(--radius-md)',
                                  color: 'var(--color-text-primary)',
                                }}
                              >
                                {FIELD_OPTIONS.map((opt) => (
                                  <option
                                    key={opt}
                                    value={opt}
                                    disabled={
                                      usedTypes.includes(opt) &&
                                      col.selectedType !== opt &&
                                      opt !== 'Do not import' &&
                                      opt !== 'Custom variable'
                                    }
                                  >
                                    {opt}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <div style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)' }}>
                                {col.samples.map((s, i) => (
                                  <div key={i}>{s || <span style={{ opacity: 0.4 }}>—</span>}</div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>

                {!columns.some(c => c.selectedType === 'Email') && (
                  <div className="ai-error-banner" style={{ marginTop: '1rem' }}>
                    <AlertCircle size={16} />
                    <span>Map at least one column to <strong>Email</strong> to continue</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Existing Contacts Tab */}
      {tab === 'existing' && (
        <div className="card">
          <div className="card-header">
            <h3><Users size={17} /> Select Contacts</h3>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              {selectedContactIds.size > 0 && (
                <span style={{ fontSize: '0.8rem', color: '#7c3aed', fontWeight: 600 }}>
                  {selectedContactIds.size} selected
                </span>
              )}
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  padding: '0.4rem 0.75rem',
                  fontSize: '0.825rem',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--color-bg-primary)',
                  color: 'var(--color-text-primary)',
                  width: '200px',
                }}
              />
            </div>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {loadingContacts ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                Loading contacts...
              </div>
            ) : filteredContacts.length === 0 ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: 'var(--color-text-muted)' }}>
                <Users size={40} style={{ margin: '0 auto 0.75rem', opacity: 0.4, display: 'block' }} />
                <p>No contacts found. Upload a CSV instead or add contacts first.</p>
              </div>
            ) : (
              <div className="table-container" style={{ maxHeight: '45vh', overflow: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedContactIds.size === filteredContacts.length && filteredContacts.length > 0}
                          onChange={toggleAll}
                        />
                      </th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Location</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredContacts.map((c) => (
                      <tr
                        key={c.id}
                        onClick={() => toggleContact(c.id)}
                        style={{ cursor: 'pointer', background: selectedContactIds.has(c.id) ? 'rgba(139, 92, 246, 0.04)' : undefined }}
                      >
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedContactIds.has(c.id)}
                            onChange={() => toggleContact(c.id)}
                          />
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <div style={{
                              width: '28px', height: '28px', borderRadius: '50%',
                              background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              fontSize: '0.75rem', fontWeight: 700
                            }}>
                              {(c.name || c.email || '?').charAt(0).toUpperCase()}
                            </div>
                            {c.name || '-'}
                          </div>
                        </td>
                        <td>{c.email}</td>
                        <td>{c.location || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Available Variables Preview */}
      {currentVars.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3><Tags size={17} /> Available Personalisation Variables</h3>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
              AI will use these in generated emails
            </span>
          </div>
          <div className="card-body">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              {currentVars.map((v) => (
                <span
                  key={v.key}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    padding: '0.35rem 0.75rem',
                    background: v.custom ? 'rgba(245, 158, 11, 0.08)' : 'rgba(99, 102, 241, 0.08)',
                    border: `1px solid ${v.custom ? 'rgba(245, 158, 11, 0.2)' : 'rgba(99, 102, 241, 0.2)'}`,
                    borderRadius: '999px',
                    fontSize: '0.8rem',
                    color: v.custom ? '#d97706' : '#6366f1',
                    fontWeight: 500,
                  }}
                >
                  <code style={{ fontSize: '0.72rem' }}>{`{{${v.key}}}`}</code>
                  <span style={{ opacity: 0.7, fontSize: '0.72rem' }}>{v.label}</span>
                </span>
              ))}
            </div>
            <p style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)', marginTop: '0.875rem' }}>
              These variables will be sent to AI so generated email content uses accurate placeholders matching your lead data.
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
